import styled from '@emotion/styled';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { OneMinuteInMillis } from '@racemap/utilities/consts/time';
import { getSplitsOfTrack } from '@racemap/utilities/functions/event';
import { isTransponder } from '@racemap/utilities/functions/starters/starters';
import { shortId } from '@racemap/utilities/functions/utils';
import type { PredictionParams } from '@racemap/utilities/types/events';
import type { AcceptedReadRust, PredictionDebugData } from '@racemap/utilities/types/trackping';
import type { Reader } from '@racemap/utilities/types/types';
import { Button, Card } from 'antd';
import type { Immutable } from 'immer';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useCurrentEvent } from 'src/lib/customHooks/useCurrentEvent';
import useSWR from 'swr';
import { useReadCountsByTransponderIds } from '../../lib/customHooks/useReadCountsByTransponderIds';
import type { CurrentEvent } from '../../store/events/events_reducers';
import { useStore } from '../../store/reducers';
import { LoadingIndicator } from '../BasicComponents/LoadingIndicator';
import { Switch } from '../BasicComponents/Switch/Switch';
import { SelectStarterInput } from '../EventEditor/SelectStarterInput/SelectStarterInput';
import { MapCard } from './AnalyseComponents/MapCard';
import { MovesChart } from './AnalyseComponents/MovesChart';
import { ReadsTable } from './AnalyseComponents/ReadsTable';
import { SHADOW_TRACK_STEP } from './AnalyseComponents/utils';
import { PredictionParamsOverride } from './PredictionParamsOverride';

const apiClient = RacemapAPIClient.fromWindowLocation();

function markReads(starterDebugData: PredictionDebugData): PredictionDebugData {
  const { acceptedReads, rejectedReads } = starterDebugData;
  return {
    ...starterDebugData,
    acceptedReads: acceptedReads.map((read) => ({
      ...read,
      signature: shortId(),
    })),
    rejectedReads: rejectedReads.map((read) => ({
      ...read,
      signature: shortId(),
    })),
  };
}

export const AnalyseSubTab: FC = () => {
  const currentEvent = useCurrentEvent();
  const [showParamsOverride, setShowParamsOverride] = useState(false);
  const navigate = useNavigate();
  const { search } = useLocation();
  const chunkedShadowtrack = useStore((s) => s.prediction.chunkedShadowTrack);

  const [selectedStarterId, setSelectedStarterId] = useState<string | null>(null);
  const [predictionParamsOverride, setPredictionParamsOverride] = useState<PredictionParams | null>(
    currentEvent?.modules.predictive.params || null,
  );

  const [readInFocus, setReadInFocus] = useState<Reader | AcceptedReadRust | null>(null);
  const [showRejectedReads, setShowRejectedPings] = useState<boolean>(false);

  // We can use the counts here if needed, or pass them down to components
  // e.g. <SelectStarterInput counts={readCountsByStarters} ... />
  const chipIds = useMemo(
    () =>
      Array.from(currentEvent?.starters?.values() ?? [])
        .filter((s) => isTransponder(s))
        .map((s) => s.appId)
        .filter((id): id is string => id != null && id !== ''),
    [currentEvent?.starters],
  );

  const readCountsByTransponderIds = useReadCountsByTransponderIds(currentEvent?.id, chipIds);

  const trackLength = useMemo(
    () => (chunkedShadowtrack?.geometry.coordinates.length || 0) * SHADOW_TRACK_STEP,
    [chunkedShadowtrack],
  );

  const splits = useMemo(
    () =>
      getSplitsOfTrack(currentEvent).sort(
        (a, b) =>
          (a.properties.offset || Number.POSITIVE_INFINITY) -
          (b.properties.offset || Number.NEGATIVE_INFINITY),
      ),
    [currentEvent],
  );

  const { data: starterDebugData, isLoading } = useSWR<PredictionDebugData | null>(
    [currentEvent, selectedStarterId, predictionParamsOverride],
    async ([currentEvent, selectedStarterId, predictionParamsOverride]: [
      Immutable<CurrentEvent>,
      string,
      PredictionParams,
    ]) => {
      if (currentEvent == null) return null;
      if (selectedStarterId == null) return null;
      if (predictionParamsOverride == null) return null;

      const starterDebugData = await apiClient
        .prediction()
        .getDebugDataByStarterAndEvent(
          currentEvent.id,
          selectedStarterId,
          predictionParamsOverride,
        );

      return markReads(starterDebugData);
    },
    { refreshInterval: OneMinuteInMillis, keepPreviousData: false },
  );

  const handleSelectedStarterIdChange = (newStarterId: string | null) => {
    setSelectedStarterId(newStarterId);
    navigate({
      search: `?starterId=${newStarterId}`,
    });
  };

  useEffect(() => {
    const urlSearch = new URLSearchParams(search);
    const starterId = urlSearch.get('starterId');

    const firstStarterId =
      currentEvent != null && currentEvent.starters != null
        ? currentEvent.starters.keys().next().value
        : null;

    if (starterId != null && starterId !== selectedStarterId) {
      handleSelectedStarterIdChange(starterId);
    } else if (selectedStarterId == null && firstStarterId != null) {
      handleSelectedStarterIdChange(firstStarterId);
    }
  }, [search, selectedStarterId]);

  if (currentEvent == null || chunkedShadowtrack == null)
    return (
      <OuterContainer>
        <InnerContainer>
          {currentEvent == null && <h2>No currentEvent!</h2>}
          {chunkedShadowtrack == null && <h2>No schadowTrack!</h2>}
        </InnerContainer>
      </OuterContainer>
    );

  const unitType = currentEvent?.playerOptions.unitType;

  return (
    <OuterContainer>
      <SelectStarterAndLoadIndicator>
        <SelectStarterInput
          value={selectedStarterId}
          onChange={handleSelectedStarterIdChange}
          type="transponder"
          showReadsAndDevice
          readCountsByTransponderIds={readCountsByTransponderIds}
        />
        <Button
          style={{ marginLeft: '8px' }}
          title="Click to do research."
          onClick={() => setShowParamsOverride(!showParamsOverride)}
        >
          ...
        </Button>
        <LoadingIndicator style={{ marginLeft: '8px' }} isLoading={isLoading} />
        <RejectSwitchBox>
          <Switch
            value={showRejectedReads}
            onChange={() => {
              setShowRejectedPings(!showRejectedReads);
            }}
            label="Show rejected reads"
          />
        </RejectSwitchBox>
      </SelectStarterAndLoadIndicator>

      <InnerContainer>
        {showParamsOverride && (
          <ParameterContainer>
            <Card>
              Predictive live tracking depends on a set of adjustable parameters that determine the
              real-time response of the forecasting model. By modifying these parameters here, you
              can instantly observe how the past event would have been predicted. That way you can
              deeply analyze past events and experiment with how the model reacts under different
              conditions.
              <PredictionParamsOverride
                eventId={currentEvent.id}
                starterDebugData={starterDebugData}
                predictionParams={currentEvent.modules.predictive.params}
                onPredictionParamsChange={setPredictionParamsOverride}
              />
            </Card>
          </ParameterContainer>
        )}

        <MovesChartCard styles={chartCardStyles}>
          <MapCard
            predictionParams={predictionParamsOverride}
            starterDebugData={starterDebugData || null}
            showRejectedReads={showRejectedReads}
          />
        </MovesChartCard>
        <ATCard>
          <MovesChart
            starterDebugData={starterDebugData || null}
            chunkedTrack={chunkedShadowtrack}
            trackLength={trackLength}
            onReadInFocus={setReadInFocus}
            currentEvent={currentEvent}
            splits={splits}
            unitType={unitType}
          />
        </ATCard>
        <ATCard>
          <ReadsTable
            starterDebugData={starterDebugData || null}
            unitType={unitType}
            readInFocus={readInFocus}
            showRejectedReads={showRejectedReads}
            onShowRejectedPing={() => setShowRejectedPings(!showRejectedReads)}
          />
        </ATCard>
      </InnerContainer>
    </OuterContainer>
  );
};

const ParameterContainer = styled.div`
  height: 100%;
  margin-top: 44px;
  overflow: hidden;
`;

const InnerContainer = styled.div`
  height: 100%;
  padding: 14px;
  margin-top: 34px;  
  overflow: hidden;  
`;

const OuterContainer = styled.div`
  margin-top: 58px;  
 `;

const SelectStarterAndLoadIndicator = styled.div` 
  z-index: 10;
  position: fixed;
  display: inline-flex;
  grid-template-columns: 500px auto;
  align-items: center;    
  margin-top: -16px;
  margin-left: 10px;
  padding: 6px;
  background-color: #ffffff88;  
`;

const chartCardStyles = { body: { overflow: 'hidden', padding: 0 } } as const;

const MovesChartCard = styled(Card)`
  margin-top: 25px;

`;

const ATCard = styled(Card)`
  margin-top: 15px;
`;

const RejectSwitchBox = styled.div` 
  flex-grow: 1;
  background-color: white;
  margin-left: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  height: 32px;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
